import {
	CalendarDate,
	getLocalTimeZone,
	isWeekend,
	toCalendarDate,
	today,
} from "@internationalized/date";
import type { DateValue } from "@internationalized/date";

/**
 * Constraints a date field can apply to the days a user may pick.
 *
 * Semantics:
 * - `disablePastDates` blocks every day before today; today itself stays
 *   selectable. Use a minimum notice of 1 to push the floor to the next
 *   business day.
 * - `minimumNoticeBusinessDays` counts forward from today, skipping weekends
 *   and public holidays, so `1` means "the next business day". Counting always
 *   skips weekends and holidays regardless of the two `disable*` flags — those
 *   only control which days remain selectable above the floor.
 * - A minimum notice implies past dates are blocked, since the floor it
 *   produces is always in the future.
 */
export interface DateConstraints {
	disableWeekends?: boolean | null;
	disablePublicHolidays?: boolean | null;
	disablePastDates?: boolean | null;
	minimumNoticeBusinessDays?: number | null;
	publicHolidays?: { date: string }[] | null;
	/** Overrides "today" — for tests. Defaults to today in the visitor's timezone. */
	referenceDate?: CalendarDate;
}

/** Australian locale, so `isWeekend` treats Saturday and Sunday as the weekend. */
const LOCALE = "en-AU";

/**
 * Upper bound on the day-by-day walk in {@link addBusinessDays}, so a
 * pathological public holiday list can never spin forever.
 */
const MAX_NOTICE_WALK_DAYS = 1000;

/**
 * Builds a lookup of `yyyy-MM-dd` strings from the CMS public holiday list.
 * Hoist this out of render loops — the alternative is re-scanning the array for
 * every candidate day the calendar paints.
 */
export const toHolidaySet = (holidays?: { date: string }[] | null) =>
	new Set((holidays ?? []).map(holiday => holiday.date).filter(Boolean));

/** `CalendarDate#toString()` is already `yyyy-MM-dd`, matching the CMS format. */
export const isPublicHoliday = (date: DateValue, holidaySet: Set<string>) =>
	holidaySet.has(toCalendarDate(date).toString());

export const isWeekendDate = (date: DateValue) => isWeekend(date, LOCALE);

/** True for any day that is neither a weekend nor a public holiday. */
const isBusinessDay = (date: CalendarDate, holidaySet: Set<string>) =>
	!isWeekendDate(date) && !isPublicHoliday(date, holidaySet);

/**
 * Steps forward from `start` by `days` business days, skipping weekends and
 * public holidays. `start` itself is never counted, so a Friday + 3 business
 * days lands on the following Wednesday.
 */
export const addBusinessDays = (start: CalendarDate, days: number, holidaySet: Set<string>) => {
	let date = start;
	let remaining = days;
	let walked = 0;

	while (remaining > 0 && walked < MAX_NOTICE_WALK_DAYS) {
		date = date.add({ days: 1 });
		walked += 1;
		if (isBusinessDay(date, holidaySet)) remaining -= 1;
	}

	return date;
};

const getReferenceDate = (constraints: DateConstraints) =>
	constraints.referenceDate ?? today(getLocalTimeZone());

/**
 * The earliest day a user may pick, or `undefined` when nothing constrains the
 * lower bound. Feed this to the field's `minValue` so the calendar can't be
 * paged back past it and typed entry gets the same floor.
 */
export const getMinSelectableDate = (constraints: DateConstraints): CalendarDate | undefined => {
	const notice = constraints.minimumNoticeBusinessDays;
	const reference = getReferenceDate(constraints);

	if (typeof notice === "number" && notice > 0) {
		return addBusinessDays(reference, notice, toHolidaySet(constraints.publicHolidays));
	}

	if (constraints.disablePastDates) return reference;

	return undefined;
};

/**
 * Single predicate shared by the calendar's `isDateUnavailable` and the
 * form-level validation, so the two can't disagree about which days are valid.
 */
export const isDateUnavailableFor = (constraints: DateConstraints) => {
	const holidaySet = toHolidaySet(constraints.publicHolidays);
	const minDate = getMinSelectableDate(constraints);

	return (date: DateValue) => {
		if (minDate && toCalendarDate(date).compare(minDate) < 0) return true;
		if (constraints.disableWeekends && isWeekendDate(date)) return true;
		if (constraints.disablePublicHolidays && isPublicHoliday(date, holidaySet)) return true;
		return false;
	};
};

/** `dd/MM/yyyy`, for user-facing validation messages. */
export const formatDateForMessage = (date: CalendarDate) =>
	`${String(date.day).padStart(2, "0")}/${String(date.month).padStart(2, "0")}/${date.year}`;
